import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { blogPosts } from "@/data/blogPosts";
import { Calendar } from "lucide-react";

const Blog = () => {
  const sortedPosts = [...blogPosts].sort((a, b) => 
    new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <SEO 
        title="Lawn Care Marketing Blog"
        description="Expert tips and strategies for growing your lawn care or landscaping business online. Learn about website design, local SEO, and digital marketing."
        keywords="lawn care marketing, lawn care blog, landscaping marketing tips, lawn care SEO"
        canonical="https://www.leafrushmarketing.com/blog/"
        ogUrl="https://www.leafrushmarketing.com/blog/"
      />
      
      <div className="flex flex-col min-h-screen">
        <section className="py-20 md:py-32">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl">
              <div className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                  Lawn Care Marketing <span className="text-primary">Blog</span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Expert tips and strategies for growing your lawn care or landscaping business online
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedPosts.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} className="block h-full group">
                    <Card className="hover:shadow-lg transition-all duration-300 h-full flex flex-col overflow-hidden border-2 hover:border-primary/20">
                      {post.heroImageUrl && (
                        <div className="w-full h-48 overflow-hidden bg-muted">
                          <img 
                            src={post.heroImageUrl} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardHeader className="flex-1">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <Calendar className="h-4 w-4" />
                          <time dateTime={post.publishDate}>
                            {formatDate(post.publishDate)}
                          </time>
                        </div>
                        <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </CardTitle>
                        <CardDescription className="text-sm line-clamp-3 leading-relaxed">
                          {post.excerpt}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          Read More
                        </Button>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
